/**
 * Format a date string for datetime-local input
 * @param isoString ISO date string or null/undefined
 * @returns Formatted date string for datetime-local input
 */
export const formatDateTimeLocal = (isoString: string | null | undefined): string => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    // Adjust for local timezone offset
    const timezoneOffset = date.getTimezoneOffset() * 60000; // Offset in milliseconds
    const localDate = new Date(date.getTime() - timezoneOffset);
    return localDate.toISOString().slice(0, 16);
  } catch {
    return '';
  }
};

/**
 * Format a date string for datetime-local input with timezone +2
 * @param isoString ISO date string or null/undefined
 * @returns Formatted date string for datetime-local input in timezone +2
 */
export const formatDateTimeLocalWithTimezone = (isoString: string | null | undefined): string => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';

    // Create a date formatter for timezone +2
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Etc/GMT-2',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    // Get formatted parts
    const parts = formatter.formatToParts(date);
    const values: { [key: string]: string } = {};
    parts.forEach(part => {
      values[part.type] = part.value;
    });

    // Format as YYYY-MM-DDThh:mm (required format for datetime-local input)
    return `${values.year}-${values.month}-${values.day}T${values.hour}:${values.minute}`;
  } catch {
    return '';
  }
};

/**
 * Convert a datetime-local input value to ISO string with timezone +2
 * @param localDateTime datetime-local input value
 * @returns ISO string adjusted for timezone +2
 */
export const convertLocalToISOWithTimezone = (localDateTime: string): string | null => {
  if (!localDateTime) return null;
  try {
    // Parse the local datetime string
    const [datePart, timePart] = localDateTime.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);

    // Create a date in UTC
    const date = new Date(Date.UTC(year, month - 1, day, hour, minute));

    // Adjust for timezone +2 (subtract 2 hours to get the correct UTC time)
    date.setUTCHours(date.getUTCHours() - 2);

    return date.toISOString();
  } catch {
    return null;
  }
};

/**
 * Format a date string with timezone +2
 * @param dateString Date string to format
 * @param short Whether to use short format (omits year)
 * @returns Formatted date string in timezone +2
 */
export const formatDateWithTimezone = (dateString: string | null | undefined, short: boolean = false): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    // Create a date formatter for timezone +2
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Etc/GMT-2', // Note: The sign is inverted in Etc/GMT-X format
      year: short ? undefined : 'numeric',
      month: short ? 'short' : 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false // Use 24-hour format
    });

    return formatter.format(date);
  } catch {
    return '';
  }
};

/**
 * Convert datetime-local input to ISO string preserving the exact local time
 * This prevents timezone offset issues by appending the local timezone
 * @param localDateTime datetime-local input value (YYYY-MM-DDTHH:mm)
 * @returns ISO string with local timezone preserved
 */
export const preserveLocalDateTime = (localDateTime: string): string | null => {
  if (!localDateTime) return null;
  try {
    // Get the local timezone offset in minutes
    const now = new Date();
    const timezoneOffsetMinutes = now.getTimezoneOffset();
    
    // Convert offset to hours and minutes with proper sign
    const offsetHours = Math.floor(Math.abs(timezoneOffsetMinutes) / 60);
    const offsetMinutes = Math.abs(timezoneOffsetMinutes) % 60;
    const offsetSign = timezoneOffsetMinutes <= 0 ? '+' : '-';
    
    // Format timezone offset as ±HH:mm
    const timezoneOffset = `${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;
    
    // Append timezone to the datetime string
    const datetimeWithTimezone = `${localDateTime}:00${timezoneOffset}`;
    
    // Convert to ISO string - this will preserve the exact input time
    const date = new Date(datetimeWithTimezone);
    return date.toISOString();
  } catch (error) {
    console.error('Error preserving local datetime:', error);
    return null;
  }
}; 